import { useMemo } from 'react';
import { auditMapDocument, type MapDocument } from '../../engine';

/**
 * Audits the project actually being published. Previously this read the 3D
 * Edit mode's global in-memory store regardless of which project's Publish
 * page was open - opening Publish without having opened this project's
 * editor this session audited an empty scene (a false "no spawn points"
 * failure), and having a *different* project open in the editor made this
 * silently audit that other project's leftover object list instead. The
 * real publish gate (assertPublishable) always re-fetches the true map from
 * the api, so nothing wrong was ever actually published - but the pre-flight
 * check a creator relies on to catch problems before publishing could be
 * flatly wrong. `map` now comes from the workspace's own loaded project.
 */
export function MapAuditPanel({ map }: { map: MapDocument | null | undefined }) {
  const report = useMemo(() => (map ? auditMapDocument(map) : null), [map]);

  if (!report) {
    return (
      <div style={{ padding: '14px 16px' }}>
        <span style={{ fontSize: 12.5, color: 'var(--ss-text-3)' }}>Loading map…</span>
      </div>
    );
  }

  const severityColor = (sev: 'error' | 'warning' | 'info') =>
    sev === 'error' ? '#f87171' : sev === 'warning' ? '#fbbf24' : '#60a5fa';

  const severityBadge = (sev: 'error' | 'warning' | 'info') =>
    sev === 'error' ? 'ERROR' : sev === 'warning' ? 'WARN' : 'INFO';

  return (
    <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Score bar */}
      <div
        style={{
          padding: '14px 16px',
          borderRadius: 10,
          background: 'rgba(22,27,34,0.8)',
          border: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: '#8b949e', display: 'block' }}>
            MAP READINESS
          </span>
          <span
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: report.score > 80 ? '#7ee787' : report.score > 50 ? '#fbbf24' : '#f87171',
            }}
          >
            {report.score} / 100
          </span>
        </div>
        <div
          style={{
            padding: '4px 10px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            background: report.passed ? 'rgba(126,231,135,0.15)' : 'rgba(248,113,113,0.15)',
            color: report.passed ? '#7ee787' : '#f87171',
            border: '1px solid ' + (report.passed ? 'rgba(126,231,135,0.3)' : 'rgba(248,113,113,0.3)'),
          }}
        >
          {report.passed ? 'PASSED' : 'ACTION REQUIRED'}
        </div>
      </div>

      {/* Issues list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {report.issues.length === 0 ? (
          <p style={{ fontSize: 12.5, color: '#7ee787', margin: 0, padding: '10px 0' }}>
            ✓ No pre-flight issues detected. Map is ready for publication.
          </p>
        ) : (
          report.issues.map((issue) => (
            <div
              key={issue.id}
              style={{
                padding: '12px 14px',
                borderRadius: 8,
                background: 'rgba(22,27,34,0.5)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderLeft: `4px solid ${severityColor(issue.severity)}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#f0f6fc' }}>{issue.title}</span>
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: severityColor(issue.severity),
                    background: 'rgba(255,255,255,0.04)',
                    padding: '2px 6px',
                    borderRadius: 4,
                  }}
                >
                  {severityBadge(issue.severity)}
                </span>
              </div>
              <span style={{ fontSize: 12, color: '#8b949e', lineHeight: 1.4 }}>{issue.description}</span>
              {issue.fixHint && (
                <span style={{ fontSize: 11, color: 'var(--ss-accent, #58a6ff)', marginTop: 2 }}>
                  💡 {issue.fixHint}
                </span>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
